// Aplica un cambio de la selección (regalo, promo de venta o código) con el PUT único de la sesión.
import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AnchorSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import { rememberedPromotionName } from '../lib/promotionNames';
import { evaluationKey } from '../api/useWizardPromotions';
import { wizardPromotionsClient } from '../api/wizardPromotions.client';
import { finishWrite, startWrite } from './settleAfterWrites';
import { applyChange, type AppliedSelection, type SelectionChange } from '../lib/applySelection';
import {
    errorCodeOf,
    isToastPromotionError,
    needsSessionRefresh,
    promotionErrorCopy,
} from '../lib/promotionErrorCopy';
import {
    recordDiscountCodeBlock,
    seedSessionPromotions,
    storePort,
    trackPendingChange,
    usePromotionSelectionStore,
    useSessionPromotionState,
} from '../store/promotionSelectionStore';

export interface ApplyPromotionsOptions {
    sessionId: string;
    context: AnchorSlotContext;
}

export function useApplyPromotions({ sessionId, context }: ApplyPromotionsOptions) {
    const qc = useQueryClient();
    const patch = usePromotionSelectionStore((s) => s.patch);
    const { synced } = useSessionPromotionState(sessionId);
    const { refreshSession } = context;
    const copyOf = (error: unknown): string =>
        promotionErrorCopy(error, (id) => rememberedPromotionName(sessionId, id));

    useEffect(() => {
        seedSessionPromotions(sessionId, context);
    });

    const flush = (): void => {
        void qc.invalidateQueries({ queryKey: evaluationKey(sessionId) });
        void refreshSession();
    };
    const mutation = useMutation<AppliedSelection, unknown, SelectionChange>({
        mutationFn: (change) =>
            applyChange(sessionId, change, storePort(sessionId), wizardPromotionsClient),
        onMutate: (change) => {
            startWrite(sessionId);
            trackPendingChange(sessionId, change, true);
        },
        onSuccess: () => {
            patch(sessionId, { reapplyError: null });
        },
        onError: (error) => {
            recordDiscountCodeBlock(sessionId, error);
            if (isToastPromotionError(error)) {
                toast.error(copyOf(error));
            }
        },
        onSettled: (_data, error, change) => {
            trackPendingChange(sessionId, change, false);
            finishWrite(sessionId, !error || needsSessionRefresh(error), flush);
        },
    });

    return {
        apply: mutation.mutateAsync,
        /** Ya se conoce lo que tiene la sesión; antes, ningún PUT sale. */
        canWrite: synced,
        isApplying: mutation.isPending,
        // Los que salen en toast no se repiten junto a la pieza.
        error:
            mutation.error && !isToastPromotionError(mutation.error)
                ? copyOf(mutation.error)
                : null,
        errorCode: errorCodeOf(mutation.error),
        resetError: mutation.reset,
    };
}
